"use strict";
import { memo, useEffect, useState } from "react";
import { Flex, TouchableArea } from "ui/src";
import { MoreHorizontal } from "ui/src/components/icons/MoreHorizontal";
import { iconSizes } from "ui/src/theme";
import { useAddToSearchHistory } from "components/TokenSelector/hooks/useAddToSearchHistory";
import { SelectorBaseList } from "components/lists/SelectorBaseList";
import { NFTCollectionOptionItem } from "components/lists/items/nfts/NFTCollectionOptionItem";
import { PoolOptionItem } from "components/lists/items/pools/PoolOptionItem";
import { TokenContextMenuVariant, TokenOptionItem } from "components/lists/items/tokens/TokenOptionItem";
import {
  TokenContextMenuAction,
  TokenOptionItemContextMenu
} from "components/lists/items/tokens/TokenOptionItemContextMenu";
import { OnchainItemListOptionType } from "components/lists/items/types";
import { ENSAddressOptionItem } from "components/lists/items/wallets/ENSAddressOptionItem";
import { UnitagOptionItem } from "components/lists/items/wallets/UnitagOptionItem";
import { WalletByAddressOptionItem } from "components/lists/items/wallets/WalletByAddressOptionItem";
import { ContextMenuTriggerMode } from "components/menus/types";
import { useUniswapContext } from "contexts/UniswapContext";
import { sendSearchOptionItemClickedAnalytics } from "features/search/SearchModal/analytics/analytics";
import { isHoverable, isWeb } from "utilities/src/platform";
import { usePrevious } from "utilities/src/react/hooks";
import noop from "utilities/src/react/noop";
import { useBooleanState } from "utilities/src/react/useBooleanState";
export const SearchModalList = memo(function _SearchModalList({
  sections,
  refetch,
  loading,
  hasError,
  emptyElement,
  errorText,
  onSelect,
  searchFilters
}) {
  const { navigateToTokenDetails, navigateToPoolDetails, navigateToExternalProfile, navigateToNftCollection } = useUniswapContext();
  const { registerSearchItem } = useAddToSearchHistory();
  const { value: isContextMenuOpen, setFalse: closeContextMenu, toggle: toggleContextMenu } = useBooleanState(false);
  const [focusedRowIndex, setFocusedRowIndex] = useState(1);
  const previousFocusedRowIndex = usePrevious(focusedRowIndex);
  useEffect(() => {
    if (isWeb && previousFocusedRowIndex !== focusedRowIndex) {
      closeContextMenu();
    }
  }, [previousFocusedRowIndex, focusedRowIndex, closeContextMenu]);
  const renderItem = ({ item, section, rowIndex }) => {
    switch (item.type) {
      case OnchainItemListOptionType.Pool:
        return <PoolOptionItem
          token0CurrencyInfo={item.token0CurrencyInfo}
          token1CurrencyInfo={item.token1CurrencyInfo}
          poolId={item.poolId}
          chainId={item.chainId}
          protocolVersion={item.protocolVersion}
          hookAddress={item.hookAddress}
          feeTier={item.feeTier}
          feeDetails={item.feeDetails}
          gaugeAddress={item.gaugeAddress}
          focusedRowControl={{
            rowIndex,
            setFocusedRowIndex,
            focusedRowIndex
          }}
          onPress={() => {
            registerSearchItem(item);
            navigateToPoolDetails({ poolAddress: item.poolId, chainId: item.chainId });
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
      case OnchainItemListOptionType.Token:
        return <TokenOptionItem
          showTokenAddress
          option={item}
          contextMenuVariant={TokenContextMenuVariant.Search}
          focusedRowControl={{
            focusedRowIndex,
            setFocusedRowIndex,
            rowIndex
          }}
          rightElement={isHoverable && rowIndex === focusedRowIndex ? <TouchableArea
            borderWidth={1}
            hoverStyle={{
              borderColor: "$surface3Hovered"
            }}
            borderRadius="$rounded12"
            onPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleContextMenu();
            }}
          >
                  <TokenOptionItemContextMenu
            actions={[
              TokenContextMenuAction.CopyAddress,
              ...isWeb ? [] : [TokenContextMenuAction.Favorite],
              TokenContextMenuAction.Swap,
              TokenContextMenuAction.Send,
              TokenContextMenuAction.Receive,
              TokenContextMenuAction.Share
            ]}
            triggerMode={ContextMenuTriggerMode.Primary}
            currency={item.currencyInfo.currency}
            isOpen={previousFocusedRowIndex === focusedRowIndex && isContextMenuOpen}
            openMenu={noop}
            closeMenu={noop}
          >
                    <Flex p="$spacing6">
                      <MoreHorizontal size={iconSizes.icon16} color="$neutral2" />
                    </Flex>
                  </TokenOptionItemContextMenu>
                </TouchableArea> : void 0}
          onPress={() => {
            registerSearchItem(item);
            navigateToTokenDetails(item.currencyInfo.currencyId);
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
      case OnchainItemListOptionType.WalletByAddress:
        return <WalletByAddressOptionItem
          walletByAddressOption={item}
          onPress={() => {
            navigateToExternalProfile({ address: item.address });
            registerSearchItem(item);
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
      case OnchainItemListOptionType.ENSAddress:
        return <ENSAddressOptionItem
          ensAddressOption={item}
          onPress={() => {
            navigateToExternalProfile({ address: item.address });
            registerSearchItem(item);
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
      case OnchainItemListOptionType.Unitag:
        return <UnitagOptionItem
          unitagOption={item}
          onPress={() => {
            navigateToExternalProfile({ address: item.address });
            registerSearchItem(item);
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
      case OnchainItemListOptionType.NFTCollection:
        return <NFTCollectionOptionItem
          collectionOption={item}
          onPress={() => {
            const { address, chainId } = item;
            navigateToNftCollection({ collectionAddress: address, chainId });
            registerSearchItem(item);
            sendSearchOptionItemClickedAnalytics({
              item,
              section,
              rowIndex,
              searchFilters
            });
            onSelect?.();
          }}
        />;
    }
  };
  return <SelectorBaseList
    focusedRowControl={{
      focusedRowIndex,
      setFocusedRowIndex
    }}
    renderItem={renderItem}
    sections={sections}
    chainFilter={searchFilters.searchChainFilter}
    refetch={refetch}
    loading={loading}
    hasError={hasError}
    emptyElement={emptyElement}
    errorText={errorText}
    keyExtractor={key}
  />;
});
function key(item) {
  switch (item.type) {
    case OnchainItemListOptionType.Pool:
      return `pool-${item.chainId}-${item.poolId}-${item.protocolVersion}-${item.hookAddress}-${item.feeTier}`;
    case OnchainItemListOptionType.Token:
      return `token-${item.currencyInfo.currency.chainId}-${item.currencyInfo.currencyId}`;
    case OnchainItemListOptionType.WalletByAddress:
      return `wallet-${item.address}`;
    case OnchainItemListOptionType.ENSAddress:
      return `ens-${item.address}`;
    case OnchainItemListOptionType.Unitag:
      return `unitag-${item.address}`;
    case OnchainItemListOptionType.NFTCollection:
      return `nft-${item.chainId}-${item.address}`;
  }
}
